<script lang="ts">
    import {
        COMMON__loading,
        DICTIONARY__hide,
        ERROR__error,
        SPEECH__tryAgain,
    } from "@app/services/i18n.messages";
    import { getMessage } from "@app/services/i18n.service";
    import { slide } from "svelte/transition";
    import { onDestroy } from "svelte";
    import LoadingInProgress from "../LoadingInProgress.svelte";
    import {
        cleanUpHtmlFragmentLoaderForTargetId,
        getHtmlFragmentLoaderForTargetId,
    } from "./HtmlFragmentLoader.store";
    import { get } from "svelte/store";
    import { createEventDispatcher } from "svelte";

    const dispatch = createEventDispatcher();

    export let targetId: string = "target_" + Math.random();
    export let showHideButton: boolean = false;
    export let loaded: boolean = false;
    export let htmlWhenHidden: string | undefined = undefined;
    export let onLoaded: () => void | undefined = undefined;

    export let url: string | undefined = undefined;
    export let targetElement: HTMLElement | undefined = undefined;

    const activatorOpenClosedClass = "fragment_loader__activator__open";

    let topElement: HTMLSpanElement | undefined;
    let contentElement: HTMLDivElement | undefined;

    const targetDisplayedUrlWriter = getHtmlFragmentLoaderForTargetId(targetId);

    function reportOnStateChange() {
        if (!targetElement) {
            return;
        }
        setTimeout(() => {
            const open = get(targetDisplayedUrlWriter) === url;

            const elementSelectorsToToggleActiveClass =
                targetElement.dataset["fragmentSetActiveClass"];

            if (elementSelectorsToToggleActiveClass) {
                document
                    .querySelectorAll<HTMLElement>(
                        elementSelectorsToToggleActiveClass
                    )
                    .forEach((element) => {
                        if (open) {
                            element.classList.add(activatorOpenClosedClass);
                        } else {
                            element.classList.remove(activatorOpenClosedClass);
                        }
                    });
            }
        }, 10);
    }

    function createFetchUrlCallback(url: string) {
        return async function doFetchUrl(resolve: any, reject: any) {
            try {
                if (!url.startsWith("/")) {
                    reject("Invalid url");
                }
                const result = await fetch(url);
                if (result.status < 200 || 300 <= result.status) {
                    console.error("error", result.statusText);
                    reject(new Error(result.statusText));
                    return;
                }
                const html = await result.text();
                resolve(html);
                if (onLoaded) {
                    setTimeout(onLoaded);
                }

                loaded = true;
            } catch (e) {
                console.error(e);
                reject(e);
            }
        };
    }
    let currentUrl: string | undefined = url;
    const urlLoadingProgress = new Map<string, Promise<string>>();

    function reload() {
        if (currentUrl && currentUrl.trim().length > 0) {
            const fetchResult = new Promise<string>(
                createFetchUrlCallback(currentUrl)
            );
            urlLoadingProgress.set(currentUrl, fetchResult);
        }
    }
    function htmlFragmentLoadedHandler() {
        if (contentElement) {
            const scripts = contentElement.querySelectorAll(
                "script[data-after-load=true]"
            );
            Array.from(scripts).forEach((script) => {
                if (script) {
                    const scriptToLoad = script.firstChild.textContent;
                    if (scriptToLoad) {
                        eval(scriptToLoad);
                    }
                }
            });
        }
        dispatch("loaded", {});
    }

    targetDisplayedUrlWriter.subscribe((newUrlToDisplay) => {
        currentUrl = newUrlToDisplay;
        if (currentUrl && currentUrl.trim().length > 0) {
            if (!urlLoadingProgress.has(currentUrl)) {
                let loadingPromise = new Promise<string>(
                    createFetchUrlCallback(newUrlToDisplay)
                );
                urlLoadingProgress.set(currentUrl, loadingPromise);
            }

            urlLoadingProgress
                .get(currentUrl)
                .then(() => setTimeout(htmlFragmentLoadedHandler, 10));
        }

        reportOnStateChange();
    });

    // if url is set, load it
    if (url) {
        targetDisplayedUrlWriter.set(url);
    }

    function onHideClick() {
        if (targetElement || topElement) {
            const elementToScrollTo = targetElement || topElement;

            setTimeout(() => {
                elementToScrollTo.scrollIntoView({
                    block: "start",
                    behavior: "smooth",
                });
                setTimeout(() => {
                    targetDisplayedUrlWriter.set(null);
                }, 300);
            }, 1);
        }
    }

    let prevCurrentUrl = currentUrl;
    let prevLoaded = loaded;
    $: {
        if (prevCurrentUrl !== currentUrl) {
            reportOnStateChange();
        }
        prevLoaded = loaded;
    }

    onDestroy(() => {
        cleanUpHtmlFragmentLoaderForTargetId(targetId);
    });
</script>

<span bind:this={topElement} />
{#if currentUrl}
    {#await urlLoadingProgress.get(currentUrl)}
        <LoadingInProgress />
    {:then resultHtml}
        <div
            transition:slide
            data-element="fragment-loader-content"
            class="fragment_expanded"
            bind:this={contentElement}
        >
            {@html resultHtml}
            {#if showHideButton}
                <div class="py-2">
                    <button
                        class="text-button-xs-quiet dir-aware-float-right"
                        on:click={onHideClick}
                        >{getMessage(DICTIONARY__hide)}</button
                    >

                    <div class="clear-both" />
                </div>
            {/if}
        </div>
    {:catch}
        <span class="text-red-600"> {getMessage(ERROR__error)} </span>
        <button on:click={reload} class="m-auto text-button-xs">
            {getMessage(SPEECH__tryAgain)}
        </button>
    {/await}
{:else if htmlWhenHidden}
    {@html htmlWhenHidden}
{/if}
